import "../../../../application/address_autocomplete.js";
import "../../../../mychart/index.js";
import "../step.js";
import "../mixins/oauth_link.js";
import "../field.js";
import "./patient/fields/date_of_birth_field.js";
import "./patient/fields/mychart_patient_proxy_field.js";

const AVAILABLE_PHONE_TYPES = ["home", "work", "mobile"];

const ADDRESS_COMPONENTS = {
  STREET_NUMBER: "streetNumber",
  STREET_NAME: "streetName",
  CITY: "localName",
  STATE: "countrySubdivision",
  ZIP: "postalCode",
};

const __PatientStepPrior = AppointmentBooking.Wizard.Views.PatientStep;

AppointmentBooking.Wizard.Views.PatientStep = class PatientStep extends (
  AppointmentBooking.Wizard.Views.Mixins.OAuthLink(AppointmentBooking.Wizard.Views.Step)
) {
  static {
    this.prototype.template = "step/patient";
    this.prototype.name = "Patient";
    this.prototype.className = "appointment-booking-wizard-patient-step";

    this.prototype.events = { change: "handleFormElementChange" };
  }

  constructor() {
    super(...arguments);
    this.beforeShow = this.beforeShow.bind(this);
    this.areRequirementsValid = this.areRequirementsValid.bind(this);
    this.initialize = this.initialize.bind(this);
    this.handleStreetLookup = this.handleStreetLookup.bind(this);
    this.render = this.render.bind(this);
    this.contextForRender = this.contextForRender.bind(this);
    this.extractAddressComponents = this.extractAddressComponents.bind(this);
    this.ageRangeRestriction = this.ageRangeRestriction.bind(this);
    this.setPatient = this.setPatient.bind(this);
    this.getPageTitle = this.getPageTitle.bind(this);
    this.initializeAddressAutocomplete = this.initializeAddressAutocomplete.bind(this);
  }

  beforeShow() {
    if (!this.model.get("login_type")) {
      return this.model.set({ login_type: "guest" });
    }
  }

  areRequirementsValid() {
    if (this.ageRangeRestriction()) {
      return false;
    }
    if (!this.areFieldsValid()) {
      return false;
    }
    if (this.model.get("login_type") === "mychart") {
      return this.model.get("patient_id") != null;
    } else {
      return true;
    }
  }

  initialize() {
    super.initialize(...arguments);

    this.dateOfBirthField = new AppointmentBooking.Wizard.Views.PatientStep.DateOfBirthField({
      model: this.model,
      condition: () => this.model.get("visit_types").length,
    });

    this.myChartEmailField = new AppointmentBooking.Wizard.Views.Field({
      model: this.model,
      template: "step/patient/fields/mychart_email",
      condition: () => this.model.get("patient_id") && !this.ageRangeRestriction(),
    });

    this.myChartPatientProxyField = new AppointmentBooking.Wizard.Views.PatientStep.MyChartPatientProxyField({
      model: this.model,
      condition: () => this.model.get("patient_proxies").length,
    });

    this.ageRangeCallout = new AppointmentBooking.Wizard.Views.Callout({
      model: this.model,
      className: "borderless",
      template: "messages/age_range",
      condition: () => this.ageRangeRestriction(),
      analytics: "message-shown",
    });

    // BEHAVIOUR

    // Backbone calls initialize from inside super(), before the constructor's bind statements run.
    Sparkle.MyChart.Events.on("mychart:login", this.handleMyChartLogin.bind(this));
    Sparkle.MyChart.Events.on("mychart:loginError", this.handleMyChartLoginError.bind(this));
    return this.listenTo(this.model, "change:login_type change:patient_id change:mychart_login_error", this.render);
  }

  // EVENT HANDLERS

  handleMyChartLogin(event, patient) {
    if (this.model.get("login_type") !== "mychart") {
      return;
    }
    this.model.set("mychart_login_error", null);
    return this.setPatient(patient);
  }

  handleMyChartLoginError(event) {
    if (this.model.get("login_type") !== "mychart") {
      return;
    }
    return this.model.set("mychart_login_error", true);
  }

  handleStreetLookup(payload) {
    const components = this.extractAddressComponents(payload.address);
    if (!components) {
      return;
    }

    this.model.set({
      street: components.streetAddress,
      zip_code: components.zip,
      city: components.city,
      state: components.state,
    });
    return this.showValidity("street", "zip_code", "city", "state");
  }

  handleFormElementChange() {
    if (this.model.get("login_type") === "guest") {
      return this.model.resetPatient();
    } // The user has changed the patient info, so any saved patient ID we had from a previous failure should be cleared in case this is a new person
  }

  // RENDERING

  render() {
    if (!this.renderable) {
      return this;
    }

    super.render(...arguments);

    this.renderField(".mychart-email-field", this.myChartEmailField);
    this.renderField(".mychart-patient-proxy-field", this.myChartPatientProxyField);
    this.renderField(".date-of-birth-field", this.dateOfBirthField);
    this.replaceContents(".age-range-callout-container", this.ageRangeCallout.render().el);
    this.initializeAddressAutocomplete("[name=street]");

    return this;
  }

  contextForRender() {
    return _.extend(super.contextForRender(...arguments), { available_phone_types: AVAILABLE_PHONE_TYPES });
  }

  // HELPER FUNCTIONS

  extractAddressComponents(address) {
    let streetAddress = [];
    if (address[ADDRESS_COMPONENTS.STREET_NUMBER]) {
      streetAddress.push(address[ADDRESS_COMPONENTS.STREET_NUMBER]);
    }
    if (address[ADDRESS_COMPONENTS.STREET_NAME]) {
      streetAddress.push(address[ADDRESS_COMPONENTS.STREET_NAME]);
    }
    streetAddress = streetAddress.join(" ");

    const city = address[ADDRESS_COMPONENTS.CITY];
    const state = address[ADDRESS_COMPONENTS.STATE];
    const zip = address[ADDRESS_COMPONENTS.ZIP];

    return { streetAddress, city, state, zip };
  }

  ageRangeRestriction() {
    let maximumCutoffDate;
    let minimumCutoffDate;
    const validAgeRange = this.model.get("valid_age_range");

    if (!validAgeRange) {
      return false;
    }

    const dateOfBirth = this.model.dateOfBirthMoment();

    if (!(dateOfBirth != null ? dateOfBirth.isValid() : undefined)) {
      return false;
    }

    if (validAgeRange.minimum_age) {
      minimumCutoffDate = moment().subtract(validAgeRange.minimum_age, "years");
    }
    if (validAgeRange.maximum_age != null) {
      maximumCutoffDate = moment().subtract(validAgeRange.maximum_age + 1, "years");
    }

    return (
      (minimumCutoffDate && dateOfBirth.isAfter(minimumCutoffDate)) ||
      (maximumCutoffDate && dateOfBirth.isBefore(maximumCutoffDate))
    );
  }

  setPatient(patient) {
    return this.model.set({
      patient_id: patient.patient_id,
      patient_id_type: patient.patient_id_type,
      email: patient.email,
      date_of_birth: this.model.constructor.normalizeDateOfBirth(patient.date_of_birth),
      patient_proxies: patient.patient_proxies,
    });
  }

  getPageTitle() {
    return `${this.I18n().patient.page_title} - ${this.model.get("appointment_slot").visit_type_name} - ${this.I18n().login_type[this.model.get("login_type")]}`;
  }

  initializeAddressAutocomplete(input) {
    input = this.$(input);

    if (!input.length || !!input.data("addressAutocomplete")) {
      return;
    }

    return input.data(
      "addressAutocomplete",
      new Sparkle.AddressAutocomplete(input, this.handleStreetLookup, { className: "field-autocomplete" }).render(),
    );
  }
};

if (__PatientStepPrior) {
  Object.assign(AppointmentBooking.Wizard.Views.PatientStep, __PatientStepPrior);
}
